import 'swiper/swiper-bundle.min.css'

import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { FadeInUp } from 'app/components/Common/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import { uniq } from 'lodash'
import React, { memo } from 'react'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { LazyLoadComponent } from 'react-lazy-load-image-component'
import SwiperCore, { Autoplay, EffectFade, Lazy, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

SwiperCore.use([Autoplay, EffectFade, Lazy, Navigation])

export interface Props {
  benefitsList?: string[]
  id: string
  languageCode: string
  image?: ImageProps
  pax?: string
  size?: string
  leftThumb?: ImageProps
  rightThumb?: ImageProps
  slides?: ImageProps[]
  title: string
  URL?: string
  weight?: number
}

export const Room = memo(function Room({
  benefitsList,
  languageCode,
  image,
  pax,
  size,
  leftThumb,
  rightThumb,
  slides,
  title,
  URL,
}: Props) {
  const section = title.replace(/[^a-zA-Z]/g, '').toLocaleLowerCase()

  return (
    <Container id={`room-${section}`} dial={5} row stretch wrap>
      <LeftSide dial={1}>
        {leftThumb || rightThumb ? (
          <Thumbs row space="between">
            {leftThumb ? (
              <Thumb>
                <LazyLoadComponent>
                  <Image {...leftThumb} />
                </LazyLoadComponent>
              </Thumb>
            ) : null}
            {rightThumb ? (
              <Thumb>
                <LazyLoadComponent>
                  <Image {...rightThumb} />
                </LazyLoadComponent>
              </Thumb>
            ) : null}
          </Thumbs>
        ) : null}
        {title ? (
          <FadeInUp>
            <Title>{title}</Title>
          </FadeInUp>
        ) : null}
        {pax || size ? (
          <FadeInUp>
            <Info row dial={5}>
              {size ? (
                <Size row dial={5}>
                  <img src="/icon_size.svg" alt="Room Size" />
                  {size}
                </Size>
              ) : null}
              {pax ? (
                <Pax row dial={5}>
                  <img src="/icon_pax.svg" alt="Room Pax" />
                  {pax}
                </Pax>
              ) : null}
            </Info>
          </FadeInUp>
        ) : null}
        {benefitsList ? (
          <FadeInUp>
            <Benefits>
              {benefitsList.map((item, index) => (
                <Benefit key={index}>
                  {
                    // @ts-ignore
                    item.label
                  }
                </Benefit>
              ))}
            </Benefits>
          </FadeInUp>
        ) : null}
        {URL ? (
          <FadeInUp>
            <CTA
              label={useVocabularyData('book-now', languageCode)}
              URL={URL}
              variant="corners"
              rel="noopener"
              target="blank"
            />
          </FadeInUp>
        ) : null}
      </LeftSide>
      <RightSide>
        {image && !slides ? (
          <LazyLoadComponent>
            <Image {...image} />
          </LazyLoadComponent>
        ) : null}
        {slides ? (
          <Slider>
            <Swiper
              allowTouchMove={true}
              effect="fade"
              lazy
              loop
              navigation={{
                nextEl: `.roomsList-next-${section}`,
                prevEl: `.roomsList-prev-${section}`,
              }}
              preloadImages={false}
            >
              {uniq(slides).map((item, index) => (
                <SwiperSlide key={index}>
                  <Image
                    className="swiper-lazy"
                    src={item.src}
                    srcSet={item.srcSet}
                    alt={item.alt}
                    width={item.width}
                    height={item.height}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {slides.length > 1 ? (
              <SliderControls className="hero-slider-controls" row>
                <Arrow dial={5} className={`roomsList-prev-${section}`}>
                  <ChevronLeft />
                </Arrow>
                <Arrow dial={5} className={`roomsList-next-${section}`}>
                  <ChevronRight />
                </Arrow>
              </SliderControls>
            ) : null}
          </Slider>
        ) : null}
      </RightSide>
    </Container>
  )
})

const Container = styled(FlexBox)`
  margin-top: 11rem;

  &:first-of-type {
    margin-top: 7.5rem;
  }
  &:nth-of-type(even) {
    > div {
      &:first-of-type {
        order: 2;
        padding-left: 7.472vw;
        padding-right: 12.639vw;

        @media (max-width: 1399px) {
          padding-left: 5vw;
          padding-right: 8vw;
        }
      }
    }

    .hero-slider-controls {
      left: unset;
      right: 0;
    }
  }

  @media (max-width: 1023px) {
    margin-top: 4.5rem;
    &:first-of-type {
      margin-top: 3.75rem;
    }
  }
`

const LeftSide = styled(FlexBox)`
  width: 50%;
  padding-right: 7.472vw;
  padding-left: 12.639vw;

  @media (max-width: 1399px) {
    padding-right: 5vw;
    padding-left: 8vw;
  }

  @media (max-width: 1199px) {
    padding-left: 1.875rem;
    padding-right: 1.875rem;
  }

  @media (min-width: 1024px) {
    justify-content: space-between;
  }

  @media (max-width: 1023px) {
    width: 100%;
    order: 2;
    margin-bottom: 5rem;
  }
`

const Thumbs = styled(FlexBox)`
  width: 100%;
  margin-bottom: 60px;

  @media (max-width: 1023px) {
    margin-bottom: 30px;
  }
`

const Thumb = styled.div`
  width: calc(50% - 0.75rem);
  padding-bottom: calc(50% - 0.75rem);
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 1023px) {
    display: none;
  }
`

const Info = styled(FlexBox)`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-weight: 600;
  font-size: 0.9375rem;
  letter-spacing: 0.1em;
  margin: 30px 0 20px;
  text-transform: uppercase;

  > div {
    &:nth-of-type(2) {
      display: flex;
      align-items: center;
      &:before {
        content: '';
        display: block;
        width: 2px;
        height: 30px;
        background: #edf4f1;
        margin: 0 35px;

        @media (max-width: 1023px) {
          height: 25px;
          margin: 0 30px;
        }
      }
    }
  }

  @media (max-width: 1023px) {
    font-size: 12px;
    margin: 20px 0 15px;
  }
`

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-weight: 500;
  font-size: 3.0625rem;

  @media (max-width: 1399px) {
    font-size: 32px;
  }

  @media (max-width: 1023px) {
    font-size: 25px;
  }
`

const Pax = styled(FlexBox)`
  img {
    width: 18px;
    height: 20px;
    margin-right: 10px;

    @media (max-width: 1023px) {
      width: 13.5px;
      height: 15px;
    }
  }
`

const Size = styled(FlexBox)`
  img {
    width: 22px;
    height: 22px;
    margin-right: 10px;

    @media (max-width: 1023px) {
      width: 15px;
      height: 15px;
    }
  }
`

const Benefits = styled.div``

const Benefit = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-weight: normal;
  font-size: 0.9375rem;
  margin: 1.15rem auto 0;
  padding-left: 1.25rem;
  position: relative;

  &:before {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    background: #53a78b;
    position: absolute;
    top: 0.25rem;
    left: 0;
    transform: rotate(45deg);
  }
`

const CTA = styled(Button)`
  color: #53a78b;
  border-color: #53a78b;

  &:before {
    background: #53a78b;
  }

  &:hover::before {
    background: #53a78b;
  }
`

const RightSide = styled.div`
  width: 50%;
  height: 72.3vh;
  max-height: 50rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 1399px) {
    max-height: 40.625rem;
  }

  @media (max-width: 1023px) {
    width: 100%;
    height: 55vw;
    max-height: none;
    min-height: 268px;
    margin-left: 1.875rem;
    margin-right: 1.875rem;
  }
`

const Slider = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;

  > .swiper-container {
    height: 100%;

    .swiper-slide {
      height: 100%;
      overflow: hidden;
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }
`

const SliderControls = styled(FlexBox)`
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 2;
  border: 1px solid #edf4f1;

  @media (max-width: 767px) {
    display: none;
  }
`

const Arrow = styled(FlexBox)`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  cursor: pointer;
  overflow: hidden;
  position: relative;
  transition: background 0.25s ease-in-out;

  &:hover {
    background: #edf4f1;
  }

  svg {
    width: auto;
    stroke: #53a78b;
    stroke-width: 1;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`
